import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

const STORAGE_KEY = '@Jurisnet:timerStartedAt';

interface TimerContextData {
  isActive: boolean;
  elapsedSeconds: number;
  start(): void;
  reset(): void;
}

const TimerContext = createContext<TimerContextData>({} as TimerContextData);

const readStartedAt = (): number | null => {
  try {
    const stored = Number(localStorage.getItem(STORAGE_KEY));
    return stored > 0 ? stored : null;
  } catch {
    return null;
  }
};

const TimerProvider: React.FC = ({ children }) => {
  const [startedAt, setStartedAt] = useState<number | null>(readStartedAt);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!startedAt) return undefined;

    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  const start = useCallback(() => {
    const timestamp = Date.now();
    localStorage.setItem(STORAGE_KEY, String(timestamp));
    setStartedAt(timestamp);
  }, []);

  const reset = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setStartedAt(null);
  }, []);

  const elapsedSeconds = startedAt
    ? Math.max(0, Math.floor((now - startedAt) / 1000))
    : 0;

  return (
    <TimerContext.Provider
      value={{
        isActive: !!startedAt,
        elapsedSeconds,
        start,
        reset,
      }}
    >
      {children}
    </TimerContext.Provider>
  );
};

function useTimer(): TimerContextData {
  return useContext(TimerContext);
}

export { TimerProvider, useTimer, STORAGE_KEY as TIMER_STORAGE_KEY };
